import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import { FadeLoader } from 'react-spinners';
import Select from 'react-select'
import { 
  getDaysInMonth,
  getDaysInWeek,
  getLast6Months,
  getAllMonthsInYear,
  generateColor
} from '../common'
import { BE_API_URL } from '../constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faShare } from '@fortawesome/free-solid-svg-icons';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const override = {
  display: "block",
  margin: "50% auto",
};

const MoneyAnalystic = () => {
  // UI State
  const [activeTab, setActiveTab] = useState('Expenses');
  const isIncome = activeTab === 'Income';

  // Date State
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);
  const [dates, setDates] = useState(getDaysInWeek());

  // Chart Data
  const data = {  
    labels: dates,
    datasets: [
    ],
  };
  const [dataByDay, setDataByDay] = useState(data);
  const [incomeDataByDay, setIncomeDataByDay] = useState(data);

  // Loading States
  const [loading, setLoading] = useState(true);

  // Amounts
  const [totalOutcomeAmount, setTotalOutcomeAmount] = useState(0);
  const [totalIncomeAmount, setTotalIncomeAmount] = useState(0);

  // User Selection
  const [selectedUserOption, setSelectedUserOption] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [pieChartData, setPieChart] = useState(data)

  // Date Range Handlers
  const selectThisWeek = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() + 6);
    setDates(getDaysInWeek());
    setStartDate(start);
    setEndDate(end);
    setLoading(true);
  };

  const selectThisMonth = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    setDates(getDaysInMonth());
    setStartDate(start);
    setEndDate(end);
    setLoading(true);
  };

  const selectLast6Months = () => {
    const last6months = getLast6Months();
    const displayMonths = last6months.map((item) => `${item.year}-${item.month < 10 ? '0' + item.month : item.month}`);
    setDates(displayMonths);
    const start = new Date(last6months[0].year, last6months[0].month - 1, 1);
    const end = new Date(last6months[last6months.length - 1].year, last6months[last6months.length - 1].month, 0);
    setStartDate(start);
    setEndDate(end);
  };

  const selectThisYear = () => {
    const allMonthsInYear = getAllMonthsInYear();
    const displayMonths = allMonthsInYear.map((item) => `${item.year}-${item.month < 10 ? '0' + item.month : item.month}`);
    setDates(displayMonths);
    const start = new Date(allMonthsInYear[0].year, allMonthsInYear[0].month - 1, 1);
    const end = new Date(allMonthsInYear[allMonthsInYear.length - 1].year, allMonthsInYear[allMonthsInYear.length - 1].month, 0);
    setStartDate(start);
    setEndDate(end);
    setLoading(true);
  };

  const selectLastMonth = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    setDates(getDaysInMonth());
    setStartDate(start);
    setEndDate(end);
    setLoading(true);
  };

  const handleSelectUserChange = (option) => {
    setSelectedUserOption(option);
  };

  const getChartData = (chartType = 'outcome') => {
    let requestApi = '';
    switch (chartType) {
      case 'outcome':
        requestApi = `${BE_API_URL}/api/money-lover/get-transaction-analystic-outcome-by-date-range?startDate=${startDate}&endDate=${endDate}&user=${selectedUserOption?.value}`;
        break;
      case 'income':
        requestApi = `${BE_API_URL}/api/money-lover/get-transaction-analystic-income-by-date-range?startDate=${startDate}&endDate=${endDate}&user=${selectedUserOption?.value}`;
        break;
      default:
        break;
    }

    let newDataSet = [];
    fetch(requestApi, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then(response => response.json())
    .then(data => {
      const totalAmount = data.reduce((total, item) => total + parseFloat(item.total_amount), 0);
      userOptions.forEach((user) => {
        const color = generateColor(user.label);
        const backgroundColor = color;
        const borderColor = color;
        const userTransactions = data.filter((item) => item.user_uuid == user.value);
        let userData = [];
        dates.forEach((date) => {
          const transaction = userTransactions.find((item) => item.day_of_month == date);
          userData.push(transaction ? transaction.total_amount || 0 : 0);
        });
        newDataSet.push({
          label: user.label,
          data: userData,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          borderWidth: 1,
        });
      });
      switch (chartType) {
        case 'outcome':
          setDataByDay({ ...dataByDay, datasets: newDataSet, labels: dates });
          setTotalOutcomeAmount(totalAmount);
          break;
        case 'income':
          setIncomeDataByDay({ ...incomeDataByDay, datasets: newDataSet, labels: dates });
          setTotalIncomeAmount(totalAmount);
          break;
        default:
          break;
      }
      setLoading(false);
    })
    .catch(error => {
      console.error('Error fetching chart data:', error);
    });
  };

  const getOutcomePieChartDataByDateRange = () => {
    fetch(`${BE_API_URL}/api/money-lover/get-transaction-analystic-income-pie-chart-data-by-date-range?startDate=${startDate}&endDate=${endDate}&user=${selectedUserOption?.value}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      const labels = data.map((item) => item.category)
      const pieDataAmount = data.map((item) => item.total_amount)
      const colors = labels.map((label) => generateColor(label))
      const pieData = {
        labels: labels,
        datasets: [
          {
            data: pieDataAmount,
            backgroundColor: colors,
            borderColor: colors,
            borderWidth: 1,
          },
        ],
      };
      setPieChart(pieData)
      setLoading(false)
    })
  }

  // Get chart data when user changes
  useEffect(() => {
    if (selectedUserOption) {
      getChartData('outcome');
      getChartData('income');
    }
  }, [selectedUserOption, startDate, endDate]);

  // Get users when component mounts
  useEffect(() => {
    fetch(`${BE_API_URL}/api/v1/getUsersInGroup`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then(response => response.json())
    .then(data => {
      let userOpts = data.users.map(u => ({ value: u.id, label: u.name }))
      userOpts.push({ value: 'undefined', label: 'All users' })
      setUserOptions(userOpts);
      if (data.users.length > 0) {
        setSelectedUserOption({ value: data.users[0].id, label: data.users[0].name });
        getChartData('outcome');
        getChartData('income');
      }
      setLoading(false);
    })
    .catch(error => {
      console.error('Error fetching users:', error);
    });
  }, []);

  // Chart options
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: isIncome ? 'Income by Date' : 'Expenses by Date',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#e5e7eb',
        },
        ticks: {
          color: '#6b7280',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6b7280',
        },
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Category Distribution',
      },
    },
  };

  // Summary data
  const summaryData = {
    total: totalIncomeAmount - totalOutcomeAmount,
    income: totalIncomeAmount,
    expenses: totalOutcomeAmount,
    transactions: dataByDay.datasets.reduce((total, dataset) => {
      return total + dataset.data.reduce((sum, value) => sum + value, 0)
    }, 0)
  };

  useEffect(() => {
    if (selectedUserOption) {
      getOutcomePieChartDataByDateRange()
    }
  }, [selectedUserOption, startDate, endDate])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="w-full max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button className="text-gray-500 hover:text-gray-700">
              <FontAwesomeIcon icon={faFilter} />
            </button>
            <span className="text-2xl font-bold">Money Analystic</span>
          </div>
          <div className="flex items-center space-x-4">
            <Select
              placeholder="Select User"
              value={selectedUserOption}
              options={userOptions}
              onChange={handleSelectUserChange}
              className="w-48"
            />
            <button className="text-gray-500 hover:text-gray-700">
              <FontAwesomeIcon icon={faShare} />
            </button>
          </div>
        </div>

        {/* Balance Display */}
        <div className="mt-4">
          <div className="text-gray-500 text-sm">Total Balance</div>
          <div className="text-4xl font-bold text-gray-900">
            {summaryData.total.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="w-full max-w-4xl mx-auto px-4 mb-8">
        <div className="bg-gray-100 rounded-full p-1">
          <button
            onClick={() => setActiveTab('Expenses')}
            className={`px-6 py-2 rounded-full transition ${
              !isIncome ? 'bg-white shadow text-red-600' : 'text-gray-400'
            }`}
          >
            Expenses
          </button>
          <button
            onClick={() => setActiveTab('Income')}
            className={`px-6 py-2 rounded-full transition ${
              isIncome ? 'bg-white shadow text-green-600' : 'text-gray-400'
            }`}
          >
            Income
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="w-full max-w-4xl mx-auto px-4 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 shadow">
            <div className="text-sm text-gray-500">Total</div>
            <div className="text-2xl font-bold text-gray-900">
              {summaryData.total.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow">
            <div className="text-sm text-gray-500">Income</div>
            <div className="text-2xl font-bold text-green-600">
              {summaryData.income.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow">
            <div className="text-sm text-gray-500">Expenses</div>
            <div className="text-2xl font-bold text-red-600">
              {summaryData.expenses.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
            </div>
          </div>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="w-full max-w-4xl mx-auto px-4 mb-8">
        <div className="grid grid-cols-5 gap-2">
          <button
            onClick={selectThisWeek}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center justify-center"
          >
            This Week
          </button>
          <button
            onClick={selectLastMonth}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center justify-center"
          >
            Last Month
          </button>
          <button
            onClick={selectThisMonth}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center justify-center"
          >
            This Month
          </button>
          <button
            onClick={selectLast6Months}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center justify-center"
          >
            Last 6 Months
          </button>
          <button
            onClick={selectThisYear}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center justify-center"
          >
            This Year
          </button>
        </div>
      </div>

      <div className="w-full max-w-4xl mx-auto px-4 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-1 gap-8 min-h-screen">
          <div className="bg-white rounded-xl shadow p-6">
            <Bar
              data={isIncome ? incomeDataByDay : dataByDay}
              options={{
                ...barChartOptions,
                maintainAspectRatio: false,
                height: 600,
              }}
            />
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <Pie
              data={pieChartData}
              options={{
                ...pieChartOptions,
                maintainAspectRatio: false
              }}
            />
          </div>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-gray-50 bg-opacity-75 flex items-center justify-center">
          <FadeLoader color="#3b82f6" cssOverride={override} size={40} />
        </div>
      )}
    </div>
  );
};

export default MoneyAnalystic;
